import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ImageIcon } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import ContactBlock from "../components/ContactBlock";
import ContactForm from "../components/ContactForm";
import ContactPickerModal, {
  useHasPublicContact,
} from "../components/ContactPickerModal";
import { formatMoney } from "../lib/pricing";

const experienceCopy = [
  {
    title: "Yours for the day",
    body: "A single group, a single yacht. Nothing shared. Miami skyline, ocean, and time you set aside for the people who matter.",
  },
  {
    title: "Considered from the first minute",
    body: "One dedicated point of contact from booking through disembarking. Departure details handled directly with you.",
  },
  {
    title: "Priced with clarity",
    body: "One line-item price. No add-ons after the fact. What you see at checkout is the number.",
  },
];

const steps = [
  { n: "01", title: "Choose your yacht", body: "Browse our fleet and pick the vessel that fits your day." },
  { n: "02", title: "Choose your date and time", body: "Availability is live. Pick a date, a start, and the length of your day." },
  { n: "03", title: "Pay and receive confirmation", body: "Secure your reservation and we'll follow up with departure details." },
];

function EmptyMediaTile({ label = "Photos coming soon", size = "md" }) {
  const height =
    size === "lg" ? "h-[420px] md:h-[520px]" : size === "sm" ? "h-40" : "aspect-[4/3]";
  return (
    <div
      className={`w-full ${height} bg-hbe-soft rounded-xl flex flex-col items-center justify-center gap-2 text-hbe-accent`}
      data-testid="empty-photos-tile"
    >
      <ImageIcon size={size === "lg" ? 40 : 28} strokeWidth={1.4} />
      <div className="text-sm">{label}</div>
    </div>
  );
}

export default function Home() {
  const [yachts, setYachts] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);
  const { hasAny, settings } = useHasPublicContact();

  useEffect(() => {
    api.get("/yachts").then((r) => setYachts(r.data)).catch(() => {});
  }, []);

  const hero = settings?.hero_image_url || null;
  const heroHeadline =
    (settings?.hero_headline_override || "").trim() ||
    "Private yacht experiences in Miami.";
  const heroSubheadline =
    (settings?.hero_subheadline_override || "").trim() ||
    "A private yacht, your people, your time.";
  const experienceImg = yachts[0]?.images?.[1];

  return (
    <div data-testid="home-page">
      {/* Hero — pulled up under the fixed nav so the yacht image bleeds to
          the top of the viewport. Layout adds pt-[52px] md:pt-[56px] to
          reserve space for the nav on every other page; the home hero
          negates that with an equal-and-opposite negative margin. */}
      <section
        className="relative overflow-hidden -mt-[52px] md:-mt-[56px]"
        data-testid="home-hero"
      >
        <div className="absolute inset-0">
          {hero ? (
            <img
              src={assetUrl(hero)}
              alt="Luxury yacht cruising Miami waters"
              className="w-full h-full object-cover"
              loading="eager"
            />
          ) : (
            /* Solid palette hero backdrop — never a stock/fake yacht image. */
            <div
              className="w-full h-full"
              style={{
                background:
                  "radial-gradient(ellipse at 30% 30%, rgba(85,122,149,0.35), transparent 60%), #062843",
              }}
              data-testid="home-hero-empty"
            />
          )}
          {hero && <div className="absolute inset-0 bg-black/45" />}
        </div>
        <div className="relative hbe-container py-28 md:py-40 lg:py-52">
          <div className="hbe-fade-up max-w-2xl">
            <div className="text-white/85 text-xs tracking-[0.28em] uppercase mb-5">
              HBE Entertainment — Miami
            </div>
            <h1 className="font-display font-bold tracking-tight leading-[1.05] text-white text-5xl sm:text-6xl lg:text-7xl">
              {heroHeadline}
            </h1>
            <p className="mt-5 text-white/85 text-base md:text-lg max-w-xl">
              {heroSubheadline}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/yachts" className="hbe-btn-primary" data-testid="hero-explore">
                Explore Yachts
              </Link>
              <button
                type="button"
                onClick={() => setContactOpen(true)}
                disabled={!hasAny}
                className={`hbe-btn-ghost hbe-btn-ghost-on-dark ${
                  !hasAny ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title={!hasAny ? "Contact info coming soon" : undefined}
                data-testid="hero-contact"
              >
                Contact HBE
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured yachts */}
      <section className="hbe-container py-20 md:py-28" data-testid="home-featured">
        <div className="max-w-2xl">
          <div className="hbe-eyebrow">The Fleet</div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl">Featured yachts</h2>
          <p className="mt-3 text-hbe-ink/70">
            Every yacht is available on a fully private basis. Rates start at
            four hours.
          </p>
        </div>

        <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {yachts.map((y) => (
            <Link
              to={`/yachts/${y.slug}`}
              key={y.id}
              className="group"
              data-testid={`featured-yacht-${y.slug}`}
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-hbe-soft">
                {y.thumbnail_url || y.images?.[0] ? (
                  <img
                    src={assetUrl(y.thumbnail_url || y.images[0])}
                    alt={`${y.name} — feature photograph`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                ) : (
                  <EmptyMediaTile />
                )}
              </div>
              <div className="mt-5">
                <h3 className="font-serif text-2xl text-hbe-primary">{y.name}</h3>
                <div className="text-sm text-hbe-ink/70 mt-1">
                  Up to {y.capacity} guests · from{" "}
                  <span className="text-hbe-primary font-medium">
                    {formatMoney(y.base_price)} / {y.minimum_duration_hours} hours
                  </span>
                </div>
                {y.description && (
                  <p className="mt-3 text-sm text-hbe-ink/70 line-clamp-3">
                    {y.description}
                  </p>
                )}
                <div className="mt-4 inline-flex items-center text-sm text-hbe-primary group-hover:underline">
                  View yacht →
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Experience */}
      <section className="bg-hbe-soft/60 py-20 md:py-28" data-testid="home-experience">
        <div className="hbe-container grid gap-12 md:grid-cols-2 items-center">
          {experienceImg ? (
            <img
              src={assetUrl(experienceImg)}
              alt="Yacht bow at sea"
              className="w-full aspect-[4/5] object-cover rounded-xl"
              loading="lazy"
            />
          ) : (
            <div className="w-full aspect-[4/5] rounded-xl bg-white flex items-center justify-center border border-hbe-line">
              <EmptyMediaTile label="Photos coming soon" />
            </div>
          )}
          <div>
            <div className="hbe-eyebrow">The HBE experience</div>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl">
              Understated. Private. Yours.
            </h2>
            <div className="mt-8 space-y-8">
              {experienceCopy.map((e) => (
                <div key={e.title}>
                  <h3 className="font-serif text-xl text-hbe-primary">{e.title}</h3>
                  <p className="mt-2 text-hbe-ink/75">{e.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Simple booking */}
      <section className="hbe-container py-20 md:py-28" data-testid="home-steps">
        <div className="max-w-2xl">
          <div className="hbe-eyebrow">How it works</div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl">
            Simple booking, in three steps
          </h2>
        </div>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="border-t-2 border-hbe-primary pt-5">
              <div className="font-serif text-3xl text-hbe-accent">{s.n}</div>
              <h3 className="font-serif text-xl mt-2">{s.title}</h3>
              <p className="mt-2 text-hbe-ink/70 text-sm">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Miami */}
      <section className="bg-hbe-primary text-white py-20 md:py-28" data-testid="home-miami">
        <div className="hbe-container max-w-3xl">
          <div className="text-white/70 text-xs tracking-[0.28em] uppercase">Miami</div>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl text-white">
            Miami-based private yacht experiences.
          </h2>
          <p className="mt-4 text-white/85 max-w-xl">
            Departure details are provided after booking is confirmed.
          </p>
        </div>
      </section>

      {/* Contact */}
      <section className="hbe-container py-20 md:py-28" data-testid="home-contact">
        <div className="grid gap-12 md:grid-cols-2 items-start">
          <div>
            <div className="hbe-eyebrow">Contact</div>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl">
              Prefer to talk it through?
            </h2>
            <p className="mt-4 text-hbe-ink/70 max-w-md">
              Text, call, or WhatsApp us — or send a note using the form.
            </p>
            <div className="mt-6">
              <ContactBlock />
            </div>
          </div>
          <ContactForm />
        </div>
      </section>

      <ContactPickerModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        settings={settings}
      />
    </div>
  );
}
